import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Wrench, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Building2,
  Cog,
  Shield,
  TrendingUp,
  Target,
  Thermometer,
  Zap
} from 'lucide-react';

export default function MarchesPublicsMaintenancePage() {
  return (
    <>
      <SEO 
        title="Marchés Publics Maintenance : Appels d'Offres Entretien et Services Techniques"
        description="Comment trouver des marchés publics de maintenance et entretien. Guide complet : maintenance bâtiments, équipements techniques, chauffage, climatisation, ascenseurs. Conseils pour entreprises de services."
        path="/marches-publics-maintenance"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics maintenance' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-900 via-amber-900 to-slate-900 py-16 text-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-300 rounded-full text-sm font-medium mb-6">
                <Wrench className="w-4 h-4" />
                Secteur Maintenance & Entretien
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Marchés Publics Maintenance : Appels d'Offres Entretien et Services Techniques
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Les collectivités et établissements publics externalisent massivement la maintenance de leurs équipements. 
                Découvrez comment identifier et remporter ces marchés récurrents à fort potentiel.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés maintenance
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '8 Md€', label: 'maintenance publique/an', icon: TrendingUp },
                { value: '+15%', label: 'externalisation croissante', icon: Target },
                { value: '12000+', label: 'marchés maintenance/an', icon: FileText },
                { value: '80%', label: 'contrats pluriannuels', icon: Clock }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Wrench className="w-8 h-8 text-orange-600" />
                Quels types de marchés publics de maintenance ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les <strong>marchés publics de maintenance</strong> couvrent l'ensemble des prestations d'entretien 
                  et de maintien en condition opérationnelle des équipements publics. Ces marchés représentent 
                  une opportunité majeure pour les entreprises de services techniques.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {[
                  {
                    icon: Building2,
                    title: 'Maintenance bâtiments',
                    items: ['Entretien multi-technique', 'Plomberie et sanitaires', 'Électricité générale', 'Menuiserie et serrurerie']
                  },
                  {
                    icon: Thermometer,
                    title: 'Génie climatique',
                    items: ['Chauffage et chaudières', 'Climatisation et VMC', 'Pompes à chaleur', 'Régulation thermique']
                  },
                  {
                    icon: Zap,
                    title: 'Équipements techniques',
                    items: ['Ascenseurs et monte-charges', 'Portes automatiques', 'Systèmes de sécurité incendie', 'Groupes électrogènes']
                  },
                  {
                    icon: Cog,
                    title: 'Maintenance industrielle',
                    items: ['Équipements de production', 'Réseaux techniques', 'Instrumentation', 'Automatismes']
                  }
                ].map((category, i) => (
                  <div key={`item-${i}`} className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <category.icon className="w-6 h-6 text-orange-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900">{category.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.items.map((item, j) => (
                        <li key={j} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Target className="w-8 h-8 text-orange-600" />
                Qui sont les acheteurs publics en maintenance ?
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: 'Collectivités territoriales',
                    description: 'Mairies, conseils départementaux et régionaux : écoles, gymnases, piscines, bâtiments administratifs.',
                    volume: '45% des marchés'
                  },
                  {
                    title: 'Établissements de santé',
                    description: 'Hôpitaux, EHPAD, cliniques : maintenance critique des équipements médicaux et techniques.',
                    volume: '25% des marchés'
                  },
                  {
                    title: 'Bailleurs sociaux',
                    description: 'OPH et ESH : maintenance du parc immobilier, chaufferies collectives, ascenseurs.',
                    volume: '20% des marchés'
                  }
                ].map((buyer, i) => (
                  <div key={`item-${i}`} className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
                    <h3 className="font-semibold text-slate-900 mb-2">{buyer.title}</h3>
                    <p className="text-slate-600 text-sm mb-3">{buyer.description}</p>
                    <div className="text-orange-600 font-medium">{buyer.volume}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 - Conseils */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Shield className="w-8 h-8 text-orange-600" />
                Réussir vos marchés de maintenance
              </h2>
              <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl p-8 text-white">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Points clés du mémoire technique</h3>
                    <ul className="space-y-3">
                      {[
                        'Organisation et délais d\'intervention (GTI/GTR)',
                        'Qualifications et habilitations du personnel',
                        'Moyens matériels et stock de pièces détachées',
                        'Traçabilité et reporting (GMAO)',
                        'Démarche environnementale et gestion des déchets'
                      ].map((item, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-orange-200 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Certifications valorisées</h3>
                    <ul className="space-y-3">
                      {[
                        'Qualibat (maintenance bâtiment)',
                        'Qualifélec (électricité)',
                        'Qualigaz (installations gaz)',
                        'QualiPAC (pompes à chaleur)',
                        'ISO 9001 / ISO 14001'
                      ].map((item, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-orange-200 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-slate-900 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Ne manquez plus aucun marché de maintenance
              </h2>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Recevez chaque semaine les appels d'offres maintenance correspondant à vos qualifications 
                et votre zone géographique.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
                    Créer mon compte gratuit
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/trouver-appels-offres">
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                    Voir les marchés du jour
                  </Button>
                </Link>
              </div>
            </div>

          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPath="/marches-publics-maintenance" />

        <Footer />
      </div>
    </>
  );
}
